import { useState } from 'react';
import { get } from 'lodash';
import Alert from '@lendi-ui/alert';
import { Body } from '@lendi-ui/typography';

import {
  BENEFIT_ENQUIRIES,
  BENEFIT_ENQUIRIES_LABELS,
  CO_APPLICANT_BENEFIT,
  CO_APPLICANT_BENEFIT_LABELS,
  CO_APPLICANT_REASON_LABELS,
  FIELD_PATHS,
} from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { CheckboxesField, CheckboxField, FunnelGridUnit, TextAreaField } from '@funnels/ui-components';

import UncontrolledRadioButtonsField from '../../../Fields/_uncontrolled/RadioButtonsField';

interface SubstantialBenefitsProps {
  isOptional?: boolean;
  isDisabled?: boolean;
  substantialBenefitIndex: number;
}

export const SubstantialBenefits = (props: SubstantialBenefitsProps) => {
  const { substantialBenefitIndex } = props;
  const [formValues, setFormValue] = useFormValues();
  const coApplicantBenefit = get(
    formValues,
    FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_CO_APPLICANT_BENEFIT(0, substantialBenefitIndex)
  );
  const hasCoApplicantReasonOther = get(
    formValues,
    FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_BENEFIT_ENQUIRIES(0, substantialBenefitIndex),
    []
  ).some(
    (_: BENEFIT_ENQUIRIES, index: number) =>
      get(
        formValues,
        FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_BENEFIT_ENQUIRY(0, substantialBenefitIndex, index)
      ) === BENEFIT_ENQUIRIES.Other
  );
  if (!hasCoApplicantReasonOther) {
    setFormValue(
      FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_BENEFIT_ENQUIRIES_DESCRIPTION(0, substantialBenefitIndex),
      undefined
    );
  }

  const [selectedCoApplicantBenefit, setSelectedCoApplicantBenefit] = useState<string>(coApplicantBenefit);

  const handleCoApplicantBenefitClick = (coApplicantBenefit: string) => {
    if (selectedCoApplicantBenefit !== coApplicantBenefit) {
      setFormValue(
        FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_BENEFIT_ENQUIRIES(0, substantialBenefitIndex),
        undefined
      );
      setFormValue(
        FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_BENEFIT_ENQUIRIES_DESCRIPTION(0, substantialBenefitIndex),
        undefined
      );
      setFormValue(
        FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_CO_APPLICANT_REASON(0, substantialBenefitIndex),
        []
      );
      setFormValue(
        FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_SUSPECT_EXPERIENCING_FINANCIAL_ABUSE(
          0,
          substantialBenefitIndex
        ),
        undefined
      );
      setFormValue(
        FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_UNDERSTANDS_CO_APPLICANT_OBLIGATIONS(
          0,
          substantialBenefitIndex
        ),
        undefined
      );
    }
    setSelectedCoApplicantBenefit(coApplicantBenefit);
    setFormValue(
      FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_CO_APPLICANT_BENEFIT(0, substantialBenefitIndex),
      coApplicantBenefit
    );
  };

  return (
    <>
      <FunnelGridUnit mb="md" size={1 / 2}>
        <Body size="md">How will you benefit from this loan?</Body>
      </FunnelGridUnit>
      <FunnelGridUnit mb="md" size={1 / 2}>
        {selectedCoApplicantBenefit === CO_APPLICANT_BENEFIT.NoSubstantialBenefit && (
          <Alert variant="warn" data-testid="alert-warn">
            Please remove the applicant who will not be included in the loan, and make any required updates to the
            Property, Income & Expenses, Assets & Liabilities, and Needs and Objectives sections. You will not be able
            to progress with the application until you have done so.
          </Alert>
        )}
        <UncontrolledRadioButtonsField
          onChange={(e) => handleCoApplicantBenefitClick(e.target.value)}
          selectedValue={selectedCoApplicantBenefit}
          radios={CO_APPLICANT_BENEFIT_LABELS}
          isBoxed={false}
        />
      </FunnelGridUnit>

      {selectedCoApplicantBenefit === CO_APPLICANT_BENEFIT.SubstantialBenefit && (
        <>
          <FunnelGridUnit mb="md" size={1 / 2}>
            <Body size="md">How will you receive a substantial benefit?</Body>
          </FunnelGridUnit>
          <FunnelGridUnit mb="md" size={1 / 2}>
            <CheckboxesField
              name={FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_BENEFIT_ENQUIRIES(0, substantialBenefitIndex)}
              checkboxes={BENEFIT_ENQUIRIES_LABELS}
              values={get(
                formValues,
                FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_BENEFIT_ENQUIRIES(0, substantialBenefitIndex),
                []
              )}
            />
            <TextAreaField
              name={FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_BENEFIT_ENQUIRIES_DESCRIPTION(
                0,
                substantialBenefitIndex
              )}
              key={get(
                formValues,
                FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_BENEFIT_ENQUIRIES(0, substantialBenefitIndex),
                []
              ).toString()}
              disableFailMessage={!hasCoApplicantReasonOther}
              label="Provide explanation"
              isDisabled={!hasCoApplicantReasonOther}
              isOptional={!hasCoApplicantReasonOther}
              isFullWidth
            />
          </FunnelGridUnit>
        </>
      )}

      {selectedCoApplicantBenefit === CO_APPLICANT_BENEFIT.NoSubstantialBenefitAndProceed && (
        <>
          <FunnelGridUnit mb="md" size={1 / 2}>
            <Body size="md">
              The co-borrower has confirmed that they understand the risks associated with entering into the loan, and
              understand the difference between being a co-borrower and a guarantor
            </Body>
          </FunnelGridUnit>
          <FunnelGridUnit mb="md" size={1 / 2}>
            <CheckboxField
              name={FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_UNDERSTANDS_CO_APPLICANT_OBLIGATIONS(
                0,
                substantialBenefitIndex
              )}
              size="md"
              checkboxLabel="I confirm"
            />
          </FunnelGridUnit>
          <FunnelGridUnit mb="md" size={1 / 2}>
            <Body size="md">Why do you wish to proceed as a co-borrower?</Body>
          </FunnelGridUnit>
          <FunnelGridUnit mb="md" size={1 / 2}>
            <CheckboxesField
              name={FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_CO_APPLICANT_REASON(0, substantialBenefitIndex)}
              checkboxes={CO_APPLICANT_REASON_LABELS}
              values={get(
                formValues,
                FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_CO_APPLICANT_REASON(0, substantialBenefitIndex)
              )}
            />
          </FunnelGridUnit>
          <FunnelGridUnit mb="md" size={1 / 2}>
            <Body size="md">During my dealings in connection with this loan application, I confirm that:</Body>
            <ul>
              <li>This applicant has stated that they are not subject to financial abuse; and</li>
              <li>
                I did not notice anything which caused me to consider that this applicant is experiencing financial
                abuse
              </li>
            </ul>
          </FunnelGridUnit>
          <FunnelGridUnit mb="md" size={1 / 2}>
            <CheckboxField
              name={FIELD_PATHS.BROKER_INTERVIEW_SUBSTANTIAL_BENEFIT_SUSPECT_EXPERIENCING_FINANCIAL_ABUSE(
                0,
                substantialBenefitIndex
              )}
              size="md"
              checkboxLabel="I confirm"
              reverseCheckboxValue={true}
            />
          </FunnelGridUnit>
        </>
      )}
    </>
  );
};
